import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { scrollToId } from "../../lib/site";

const FOCUS_POINTS = [
    "Relaxation",
    "Rejuvenation",
    "Premium Ambience",
    "Professional Service",
    "Privacy",
    "Personalized Wellness",
];

const About = () => {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: imgRef,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative overflow-hidden py-28 md:py-36"
        >
            <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12 lg:gap-12">
                <div className="relative lg:col-span-5">
                    <Reveal>
                        <div
                            ref={imgRef}
                            className="relative aspect-[4/5] overflow-hidden"
                        >
                            <motion.img
                                style={{ y }}
                                src="/images/about.jpg"
                                alt="Serene lounge corner at Flammingo Luxurious Spa, spa in Vashi Navi Mumbai"
                                className="h-full w-full scale-110 object-cover"
                            />
                            <div className="absolute inset-0 bg-emerald-950/15" />
                        </div>
                        <div
                            className="pointer-events-none absolute -bottom-5 -right-5 -z-10 h-full w-full border border-gold/30"
                            aria-hidden="true"
                        />
                    </Reveal>
                </div>

                <div className="flex flex-col justify-center lg:col-span-7 lg:pl-10">
                    <Reveal>
                        <Overline>About Us</Overline>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-cream sm:text-5xl">
                            Welcome to{" "}
                            <span className="italic text-gold-light">
                                Flammingo Luxurious Spa
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-2xl text-base font-light leading-relaxed tracking-wide text-sage md:text-lg">
                            Tucked into the heart of Vashi, Navi Mumbai,
                            Flammingo Luxurious Spa is a serene wellness
                            destination created for guests who wish to step
                            away from the city&apos;s pace. Every detail — from
                            the hushed, emerald-toned interiors to the warmth
                            of our welcome — is composed for one purpose: your
                            quiet, refined escape.
                        </p>
                        <p className="mt-5 max-w-2xl text-base font-light leading-relaxed tracking-wide text-sage md:text-lg">
                            Here, time slows. Each visit is personal, private
                            and thoughtfully shaped around the way you wish to
                            unwind.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <ul className="mt-10 grid max-w-2xl grid-cols-1 gap-x-10 sm:grid-cols-2">
                            {FOCUS_POINTS.map((point) => (
                                <li
                                    key={point}
                                    className="flex items-center gap-4 border-t border-gold/15 py-4"
                                >
                                    <span
                                        className="h-1.5 w-1.5 rotate-45 bg-gold"
                                        aria-hidden="true"
                                    />
                                    <span className="text-sm uppercase tracking-[0.2em] text-cream/90">
                                        {point}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <button
                            type="button"
                            data-testid="about-discover-button"
                            onClick={() => scrollToId("#services")}
                            className="group mt-10 inline-flex w-fit items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-gold transition-colors duration-300 hover:text-gold-light"
                        >
                            Discover our experiences
                            <ArrowUpRight
                                size={16}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </button>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default About;
